import React, { useEffect, useState } from "react";
import {
  PRODUCT_THEMES,
  PRODUCT_THEME_CHANGED_EVENT,
  applyProductTheme,
  readStoredProductThemeId,
  getProductThemeById,
} from "./themes";

/**
 * Product theme picker — rounded color icons (10 themes).
 */
export default function ProductThemePicker({ canEdit = true, compact = false }) {
  const [activeId, setActiveId] = useState(() => readStoredProductThemeId());

  useEffect(() => {
    const sync = (event) => {
      const next = event?.detail?.themeId || readStoredProductThemeId();
      setActiveId(next);
    };
    window.addEventListener(PRODUCT_THEME_CHANGED_EVENT, sync);
    return () => window.removeEventListener(PRODUCT_THEME_CHANGED_EVENT, sync);
  }, []);

  const onSelect = (id) => {
    if (!canEdit) return;
    const applied = applyProductTheme(id, { persist: true });
    setActiveId(applied);
  };

  const activeTheme = getProductThemeById(activeId);

  return (
    <div className="space-y-3">
      {!compact ? (
        <div>
          <h3 className="ds-h4 text-[hsl(var(--ds-fg))]">Product theme color</h3>
          <p className="ds-caption mt-1 text-[hsl(var(--ds-muted-fg))]">
            Pick a vibe. Active:{" "}
            <span className="font-semibold text-[hsl(var(--ds-fg))]">
              {activeTheme.name}
            </span>
            {activeTheme.isDefault ? " (default)" : ""}.
          </p>
        </div>
      ) : (
        <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
          Active:{" "}
          <span className="font-semibold text-[hsl(var(--ds-fg))]">
            {activeTheme.name}
          </span>
        </p>
      )}

      <div
        className="flex flex-wrap justify-center gap-3"
        role="listbox"
        aria-label="Product theme colors"
      >
        {PRODUCT_THEMES.map((theme) => {
          const active = theme.id === activeId;
          return (
            <button
              key={theme.id}
              type="button"
              role="option"
              aria-selected={active}
              disabled={!canEdit}
              title={`${theme.name}${theme.isDefault ? " (default)" : ""}`}
              aria-label={`${theme.name}${theme.isDefault ? ", default" : ""}${
                active ? ", selected" : ""
              }`}
              onClick={() => onSelect(theme.id)}
              className={`group relative inline-flex h-10 w-10 items-center justify-center rounded-full transition ${
                !canEdit ? "cursor-not-allowed opacity-60" : "cursor-pointer"
              } ${
                active
                  ? "scale-110 ring-2 ring-[hsl(var(--ds-primary))] ring-offset-2 ring-offset-[hsl(var(--ds-surface))]"
                  : "hover:scale-105 hover:ring-2 hover:ring-[hsl(var(--ds-border))] hover:ring-offset-2 hover:ring-offset-[hsl(var(--ds-surface))]"
              }`}
            >
              <span
                className="absolute inset-0 rounded-full border border-black/10 shadow-sm"
                style={{
                  background: `linear-gradient(145deg, ${theme.swatch.primary} 55%, ${theme.swatch.secondary} 55%)`,
                }}
                aria-hidden
              />
              <span
                className="absolute bottom-0.5 right-0.5 h-2.5 w-2.5 rounded-full border border-white/80"
                style={{ background: theme.swatch.accent }}
                aria-hidden
              />
              {theme.isDefault ? (
                <span
                  className="absolute -top-0.5 -left-0.5 h-2 w-2 rounded-full bg-[hsl(var(--ds-info))] ring-1 ring-[hsl(var(--ds-surface))]"
                  title="Default"
                  aria-hidden
                />
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
